import { ConnectionState, RoomEvent, Track } from 'livekit-client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useConnectionState, useRoomContext } from '@livekit/components-react';
import { useTranslation } from 'react-i18next';
import { AiOrb } from '../AiOrb/AiOrb';
import { useAgentState } from '../hooks/useAgentState';
import styles from './AgentFeed.module.scss';

export function AgentFeed() {
  const { t } = useTranslation();
  const connectionState = useConnectionState();
  const room = useRoomContext();
  const { agentState, isAgentSpeaking, isUserSpeaking } = useAgentState();
  const [hasAgent, setHasAgent] = useState(false);
  const [audioLevels, setAudioLevels] = useState<number[]>([
    0, 0, 0, 0, 0, 0, 0,
  ]);

  // Audio analysis refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const currentSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);

  // Process audio — use RMS amplitude with a noise gate for voice responsiveness
  const processAudio = useCallback(() => {
    if (!analyserRef.current) return;

    const analyser = analyserRef.current;
    const bufferLength = analyser.fftSize;
    const dataArray = new Float32Array(bufferLength);
    analyser.getFloatTimeDomainData(dataArray);

    // Calculate RMS (root mean square) amplitude
    let sumSquares = 0;
    for (let i = 0; i < bufferLength; i++) {
      sumSquares += dataArray[i] * dataArray[i];
    }
    const rms = Math.sqrt(sumSquares / bufferLength);

    // Noise gate — ignore anything below ambient noise floor
    const noiseFloor = 0.01;
    const gated = Math.max(0, rms - noiseFloor);

    // Normalize to 0-1 range with boost
    const normalized = Math.min(1, gated * 8);

    // Distribute across 7 bands with slight variation for visual interest
    const bandsCount = 7;
    const levels = Array.from({ length: bandsCount }, (_, i) => {
      const offset = 0.8 + 0.4 * Math.sin(Date.now() * 0.006 + i * 1.2);
      return Math.min(1, normalized * offset);
    });

    setAudioLevels(levels);
    animationFrameRef.current = requestAnimationFrame(processAudio);
  }, []);

  // Setup audio analysis for a given MediaStream
  const setupAudioAnalysis = useCallback(
    (stream: MediaStream) => {
      // Clean up previous source
      if (currentSourceRef.current) {
        currentSourceRef.current.disconnect();
        currentSourceRef.current = null;
      }

      // Create audio context if needed
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext();
      }

      const audioContext = audioContextRef.current;

      // Create analyser if needed
      if (!analyserRef.current) {
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.4;
        analyserRef.current = analyser;
      }

      // Connect new source
      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyserRef.current);
      currentSourceRef.current = source;

      // Start processing if not already running
      if (!animationFrameRef.current) {
        processAudio();
      }
    },
    [processAudio]
  );

  // Stop audio analysis
  const stopAudioAnalysis = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (currentSourceRef.current) {
      currentSourceRef.current.disconnect();
      currentSourceRef.current = null;
    }
    setAudioLevels([0, 0, 0, 0, 0, 0, 0]);
  }, []);

  // Check for agent presence
  useEffect(() => {
    const checkForAgents = () => {
      const hasRemoteParticipants = room.remoteParticipants.size > 0;
      setHasAgent(hasRemoteParticipants);
    };

    checkForAgents();

    room.on(RoomEvent.ParticipantConnected, checkForAgents);
    room.on(RoomEvent.ParticipantDisconnected, checkForAgents);

    return () => {
      room.off(RoomEvent.ParticipantConnected, checkForAgents);
      room.off(RoomEvent.ParticipantDisconnected, checkForAgents);
    };
  }, [room]);

  // Setup audio analysis based on who is speaking
  useEffect(() => {
    if (isAgentSpeaking) {
      // Get agent's audio track
      const agentParticipant = Array.from(room.remoteParticipants.values())[0];
      if (agentParticipant) {
        const audioTrack = agentParticipant.getTrackPublication(
          Track.Source.Microphone
        )?.track;
        if (audioTrack?.mediaStream) {
          setupAudioAnalysis(audioTrack.mediaStream);
          return;
        }
      }
    } else if (isUserSpeaking) {
      // Get local participant's audio track
      const localAudioTrack = room.localParticipant.getTrackPublication(
        Track.Source.Microphone
      )?.track;
      if (localAudioTrack?.mediaStream) {
        setupAudioAnalysis(localAudioTrack.mediaStream);
        return;
      }
    }

    // No one speaking - stop analysis
    stopAudioAnalysis();
  }, [
    isAgentSpeaking,
    isUserSpeaking,
    room,
    setupAudioAnalysis,
    stopAudioAnalysis,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAudioAnalysis();
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      analyserRef.current = null;
    };
  }, [stopAudioAnalysis]);

  if (!hasAgent) {
    return (
      <p className={styles.tailoringInterviewExperience}>
        {t('aiInterview.tailoringExperience')}
      </p>
    );
  }

  return (
    <div className={styles.orbContainer}>
      {connectionState === ConnectionState.Connected && (
        <AiOrb state={agentState} audioLevels={audioLevels} />
      )}
    </div>
  );
}
